<template>
  <li class="expansion-panel">
    <div
      v-if="$slots['header']"
      class="expansion-panel__header"
      @click="onToggle"
    >
      <slot
        name="header"
        :active="isActive"
      />

      <Icon
        class="expansion-panel__arrow-icon"
        :name="isActive ? 'mono-chevron-up' : 'mono-chevron-down'"
      />
    </div>

    <transition
      v-if="playAnimations"
      name="expansion-panel"
      @enter="onTransitionStart"
      @after-enter="onTransitionEnd"
      @before-leave="onTransitionStart"
      @after-leave="onTransitionEnd"
    >
      <div
        v-show="isActive"
        class="expansion-panel__content"
      >
        <slot name="content" />
      </div>
    </transition>
    <template v-else>
      <div
        v-show="isActive"
        class="expansion-panel__content"
      >
        <slot name="content" />
      </div>
    </template>
  </li>
</template>

<script lang="ts">
export default {
  name: 'ExpansionPanel',
}
</script>

<script setup lang="ts">
import { computed, watch, inject } from 'vue'

import Icon from '@/uiKit/Icon/Icon.vue'
import type { ParentState } from '@/components/Common/ExpansionPanels/ExpansionPanels.vue'

const props = // eslint-disable-next-line vue/define-props-declaration
  defineProps({
    id: { required: true, type: String },
    activeByDefault: { type: Boolean, default: false },
    playAnimations: { type: Boolean, default: true },
  })

// eslint-disable-next-line vue/define-emits-declaration
const emit = defineEmits(['click'])

const expansionPanelsState = inject<ParentState>('expansionPanelsState', { active: {} })

const isActive = computed<boolean>(() => !!expansionPanelsState.active[props.id])

const onId = (): void => {
  expansionPanelsState.active[props.id] = props.activeByDefault
}

const onTransitionStart = (el: Element): void => {
  if (!(el instanceof HTMLElement)) {
    return
  }
  el.style.height = el.scrollHeight + 'px'
}

const onTransitionEnd = (el: Element): void => {
  if (!(el instanceof HTMLElement)) {
    return
  }
  el.style.height = ''
}

const onToggle = (): void => {
  emit('click', props.id)
  expansionPanelsState.active[props.id] = !isActive.value
}

watch(() => props.id, onId, { immediate: true })
</script>

<style lang="scss" scoped>
.expansion-panel {
  height: 100%;
}

.expansion-panel__header {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: 8px calc($defaultSpace / 2);
  cursor: pointer;
}

.expansion-panel__arrow-icon {
  height: 6px;
  transition: transform 0.2s ease;
  color: var(--color-icon-subtlest);
}

.expansion-panel__content {
  height: 100%;
  display: grid;
  grid-template-rows: 1fr;
}

.expansion-panel-enter-active,
.expansion-panel-leave-active {
  will-change: height, opacity;
  transition:
    height 0.3s ease,
    opacity 0.3s ease;
  overflow: hidden;
}
.expansion-panel-enter,
.expansion-panel-leave-to {
  height: 0 !important;
  opacity: 0;
}
</style>
